// supabase/functions/google-api-service/helpers.ts
// Shared helper functions available to template resolvers and email body templates
// via the `helpers` object in the sandboxed scope.

const EU_COUNTRIES = new Set([
  'AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR',
  'HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK',
]);

// ── Loose row shapes ────────────────────────────────────────────────────
// Values reach these helpers as raw PostgREST JSON (camelCased by
// `keysToCamelCase`) or straight out of a template resolver's scope, so every
// field is optional and nullable. snake_case twins are declared only where the
// code below deliberately reads both spellings. The `[key: string]: unknown`
// index signature preserves the pass-through fields templates rely on (several
// helpers spread the whole row) without falling back to `any`.

export interface ContactLike {
  isDeleted?: boolean | null;
  email?: string | null;
  purposes?: string[] | null;
  fax?: string | null;
  phone?: string | null;
  [key: string]: unknown;
}

export interface CompanyLike {
  countryCode?: string | null;
  country_code?: string | null;
  street?: string | null;
  zipCode?: string | null;
  city?: string | null;
  contacts?: ContactLike[] | null;
  paymentTerms?: number | null;
  [key: string]: unknown;
}

export interface CountryLike {
  code?: string | null;
  nameEn?: string | null;
  namePl?: string | null;
  languageCode?: string | null;
  [key: string]: unknown;
}

export interface ProductLike {
  destinationNationalCode?: string | null;
  destination_national_code?: string | null;
  wasteCode?: string | null;
  waste_code?: string | null;
  hsCode?: string | null;
  hs_code?: string | null;
  invoiceNamesUnload?: Record<string, string> | null;
  invoiceNamesLoad?: Record<string, string> | null;
  namePl?: string | null;
  name?: string | null;
  [key: string]: unknown;
}

/** Anything carrying a price triple — transports and transaction pricing rows. */
export interface PriceLike {
  price?: number | null;
  priceCurrency?: string | null;
  price_currency?: string | null;
  priceType?: string | null;
  price_type?: string | null;
  [key: string]: unknown;
}

export interface TransportLike extends PriceLike {
  transportType?: string | null;
  vehiclePlate?: string | null;
  containerNumber?: string | null;
  driverName?: string | null;
  driverPhone?: string | null;
  sequenceNumber?: number | null;
  transportCompany?: CompanyLike | null;
}

export interface FileLike {
  isLatest?: boolean | null;
  documentType?: string | null;
  documentStatus?: string | null;
  reviewStatus?: string | null;
  [key: string]: unknown;
}

export interface UserLike {
  fullName?: string | null;
  full_name?: string | null;
  phone?: string | null;
  email?: string | null;
  [key: string]: unknown;
}

/**
 * Returns true if the given ISO 3166-1 alpha-2 country code is an EU member state.
 */
export function isEuCountry(countryCode: string | null | undefined): boolean {
  if (!countryCode) return false;
  return EU_COUNTRIES.has(countryCode.toUpperCase());
}

/**
 * Returns the appropriate waste code for documents:
 * - Product has `destination_national_code` set → use it (manual override)
 * - EU destination → Basel/EU waste code (waste_code)
 * - Non-EU destination → HS code
 * Pass the destination company (importer or unloadPlace) as the second argument.
 */
export function nationalCode(
  product: ProductLike | null | undefined,
  destinationCompany: CompanyLike | null | undefined,
): string {
  if (!product) return '';
  const override = product.destinationNationalCode ?? product.destination_national_code ?? null;
  if (override) return override;
  const countryCode = destinationCompany?.countryCode ?? destinationCompany?.country_code ?? null;
  if (isEuCountry(countryCode)) {
    return product.wasteCode ?? product.waste_code ?? '';
  }
  return product.hsCode ?? product.hs_code ?? product.wasteCode ?? product.waste_code ?? '';
}

const TIMEZONE = 'Europe/Warsaw';

/**
 * Format date string to YYYY-MM-DD in Polish timezone.
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const d = new Date(dateString);
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    timeZone: TIMEZONE,
  }).formatToParts(d);
  // All three parts are guaranteed by the options above; throwing keeps the
  // previous behaviour (reading `.value` off undefined also threw a TypeError).
  const part = (type: Intl.DateTimeFormatPartTypes): string => {
    const found = parts.find(p => p.type === type);
    if (!found) throw new TypeError(`Intl.DateTimeFormat produced no "${type}" part`);
    return found.value;
  };
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * Format time to HH:MM. Accepts "HH:MM:SS" (time column) or ISO datetime.
 * No timezone conversion — displays as stored in DB.
 */
export function formatTime(value: string | null | undefined): string {
  if (!value) return '';
  // "HH:MM:SS" or "HH:MM" from a time column — take first 5 chars
  if (/^\d{2}:\d{2}/.test(value)) return value.substring(0, 5);
  // ISO datetime — extract time portion
  const match = value.match(/T(\d{2}:\d{2})/);
  return match ? match[1] : '';
}

// Weekday arrays are indexed by `Date.prototype.getDay()`: [Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday].
// Covers all 24 EU official languages so email templates render fully in-language (no Polish/English fallback).
const DAY_LOCATIVE: Record<string, string[]> = {
  pl: ['w niedzielę', 'w poniedziałek', 'we wtorek', 'w środę', 'w czwartek', 'w piątek', 'w sobotę'],
  cs: ['v neděli', 'v pondělí', 'v úterý', 've středu', 've čtvrtek', 'v pátek', 'v sobotu'],
  sk: ['v nedeľu', 'v pondelok', 'v utorok', 'v stredu', 'vo štvrtok', 'v piatok', 'v sobotu'],
  en: ['on Sunday', 'on Monday', 'on Tuesday', 'on Wednesday', 'on Thursday', 'on Friday', 'on Saturday'],
  de: ['am Sonntag', 'am Montag', 'am Dienstag', 'am Mittwoch', 'am Donnerstag', 'am Freitag', 'am Samstag'],
  fr: ['le dimanche', 'le lundi', 'le mardi', 'le mercredi', 'le jeudi', 'le vendredi', 'le samedi'],
  es: ['el domingo', 'el lunes', 'el martes', 'el miércoles', 'el jueves', 'el viernes', 'el sábado'],
  it: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
  pt: ['no domingo', 'na segunda-feira', 'na terça-feira', 'na quarta-feira', 'na quinta-feira', 'na sexta-feira', 'no sábado'],
  nl: ['op zondag', 'op maandag', 'op dinsdag', 'op woensdag', 'op donderdag', 'op vrijdag', 'op zaterdag'],
  sv: ['på söndag', 'på måndag', 'på tisdag', 'på onsdag', 'på torsdag', 'på fredag', 'på lördag'],
  da: ['på søndag', 'på mandag', 'på tirsdag', 'på onsdag', 'på torsdag', 'på fredag', 'på lørdag'],
  fi: ['sunnuntaina', 'maanantaina', 'tiistaina', 'keskiviikkona', 'torstaina', 'perjantaina', 'lauantaina'],
  et: ['pühapäeval', 'esmaspäeval', 'teisipäeval', 'kolmapäeval', 'neljapäeval', 'reedel', 'laupäeval'],
  lv: ['svētdienā', 'pirmdienā', 'otrdienā', 'trešdienā', 'ceturtdienā', 'piektdienā', 'sestdienā'],
  lt: ['sekmadienį', 'pirmadienį', 'antradienį', 'trečiadienį', 'ketvirtadienį', 'penktadienį', 'šeštadienį'],
  ro: ['duminică', 'luni', 'marți', 'miercuri', 'joi', 'vineri', 'sâmbătă'],
  bg: ['в неделя', 'в понеделник', 'във вторник', 'в сряда', 'в четвъртък', 'в петък', 'в събота'],
  el: ['την Κυριακή', 'τη Δευτέρα', 'την Τρίτη', 'την Τετάρτη', 'την Πέμπτη', 'την Παρασκευή', 'το Σάββατο'],
  hr: ['u nedjelju', 'u ponedjeljak', 'u utorak', 'u srijedu', 'u četvrtak', 'u petak', 'u subotu'],
  sl: ['v nedeljo', 'v ponedeljek', 'v torek', 'v sredo', 'v četrtek', 'v petek', 'v soboto'],
  hu: ['vasárnap', 'hétfőn', 'kedden', 'szerdán', 'csütörtökön', 'pénteken', 'szombaton'],
  ga: ['Dé Domhnaigh', 'Dé Luain', 'Dé Máirt', 'Dé Céadaoin', 'Déardaoin', 'Dé hAoine', 'Dé Sathairn'],
  mt: ['nhar il-Ħadd', 'nhar it-Tnejn', 'nhar it-Tlieta', 'nhar l-Erbgħa', 'nhar il-Ħamis', 'nhar il-Ġimgħa', 'nhar is-Sibt'],
};

const DAY_NAMES: Record<string, string[]> = {
  pl: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'],
  cs: ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'],
  sk: ['nedeľa', 'pondelok', 'utorok', 'streda', 'štvrtok', 'piatok', 'sobota'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  fr: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
  es: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  it: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
  pt: ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'],
  nl: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'],
  sv: ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'],
  da: ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'],
  fi: ['sunnuntai', 'maanantai', 'tiistai', 'keskiviikko', 'torstai', 'perjantai', 'lauantai'],
  et: ['pühapäev', 'esmaspäev', 'teisipäev', 'kolmapäev', 'neljapäev', 'reede', 'laupäev'],
  lv: ['svētdiena', 'pirmdiena', 'otrdiena', 'trešdiena', 'ceturtdiena', 'piektdiena', 'sestdiena'],
  lt: ['sekmadienis', 'pirmadienis', 'antradienis', 'trečiadienis', 'ketvirtadienis', 'penktadienis', 'šeštadienis'],
  ro: ['duminică', 'luni', 'marți', 'miercuri', 'joi', 'vineri', 'sâmbătă'],
  bg: ['неделя', 'понеделник', 'вторник', 'сряда', 'четвъртък', 'петък', 'събота'],
  el: ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'],
  hr: ['nedjelja', 'ponedjeljak', 'utorak', 'srijeda', 'četvrtak', 'petak', 'subota'],
  sl: ['nedelja', 'ponedeljek', 'torek', 'sreda', 'četrtek', 'petek', 'sobota'],
  hu: ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'],
  ga: ['Domhnach', 'Luan', 'Máirt', 'Céadaoin', 'Déardaoin', 'Aoine', 'Satharn'],
  mt: ['Il-Ħadd', 'It-Tnejn', 'It-Tlieta', 'L-Erbgħa', 'Il-Ħamis', 'Il-Ġimgħa', 'Is-Sibt'],
};

/**
 * Get day name in locative case for the given language.
 * pl: "w poniedziałek", cz: "v pondělí", sk: "v pondelok", en: "on Monday"
 */
export function getDayLocative(dateString: string | null | undefined, lang: string = 'pl'): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const names = DAY_LOCATIVE[lang] || DAY_LOCATIVE.pl;
  return names[date.getDay()];
}

/**
 * Get day name for the given language.
 */
export function getDayName(dateString: string | null | undefined, lang: string = 'en'): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const names = DAY_NAMES[lang] || DAY_NAMES.en;
  return names[date.getDay()];
}

/**
 * Format company address as single string: "street, zipCode city"
 */
export function formatCompanyAddress(company: CompanyLike | null | undefined): string {
  if (!company) return '';
  return `${company.street}, ${company.zipCode} ${company.city}`;
}

/**
 * Format transport means description based on transport type
 */
export function formatTransportMeans(transport: TransportLike | null | undefined): string {
  if (!transport) return '';
  const type = transport.transportType?.toLowerCase();
  switch (type) {
    case 'truck':
      return `Truck: ${transport.vehiclePlate}` + (transport.containerNumber ? ` (${transport.containerNumber})` : '');
    case 'ship':
      return `Ship: ${transport.containerNumber}`;
    case 'train':
      return `Train: ${transport.containerNumber}`;
    default:
      return '';
  }
}

/**
 * Extract contact emails from a company's nested contacts array.
 * Filters by purpose and active status (is_deleted = false).
 */
export function getContactEmails(company: CompanyLike | null | undefined, purpose: string): string[] {
  if (!company?.contacts) return [];
  return company.contacts
    .filter((c): c is ContactLike & { email: string } =>
      !c.isDeleted &&
      Boolean(c.email) &&
      Boolean(c.purposes?.includes(purpose))
    )
    .map((c) => c.email);
}

/**
 * Get the first contact from a company matching a given purpose.
 * Defaults to 'on_annex_doc' purpose.
 */
export function docContact(company: CompanyLike | null | undefined, purpose = 'on_annex_doc'): ContactLike | null {
  if (!company?.contacts) return null;
  const contact = company.contacts.find((c) =>
    !c.isDeleted && Boolean(c.purposes?.includes(purpose))
  ) || null;
  if (!contact) return null;
  return { ...contact, fax: contact.fax || contact.phone || null };
}

/**
 * Returns a copy of the company with `contactPerson` set to the first
 * contact matching the given purpose. Returns null if company is null.
 */
export function withDocContact(company: CompanyLike | null | undefined, purpose = 'on_annex_doc'): CompanyLike | null {
  if (!company) return null;
  return { ...company, contactPerson: docContact(company, purpose) };
}

/**
 * Filter transaction files by document types and is_latest flag.
 * Returns file objects matching any of the given doc types.
 */
export function filterFiles(files: FileLike[] | null | undefined, docTypes: string[]): FileLike[] {
  if (!files || !Array.isArray(files)) return [];
  if (docTypes.length === 0) return [];
  return files.filter((f) =>
    f.isLatest &&
    docTypes.includes(f.documentType as string)
  );
}

/**
 * Select the best approved file for a given document type:
 * - Prefers original (documentStatus === 'original') with reviewStatus === 'approved'
 * - Falls back to copy (documentStatus === 'copy') with reviewStatus === 'approved'
 * - Returns null if neither exists
 */
export function selectApprovedFile(files: FileLike[] | null | undefined, docType: string): FileLike | null {
  if (!files || !Array.isArray(files)) return null;
  const candidates = files.filter((f) => f.isLatest && f.documentType === docType && f.reviewStatus === 'approved');
  return candidates.find((f) => f.documentStatus === 'original')
    ?? candidates.find((f) => f.documentStatus === 'copy')
    ?? null;
}


/**
 * Returns the opening HTML of the branded email layout: outer wrapper + white card + logo header + accent line + body cell opening.
 * Use before <!-- BODY_START --> marker in templates.
 */
export function EMAIL_HEADER(): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
</head>
<body style="margin:0;padding:0;background:#f0f4fb;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f4fb;">
    <tr><td align="center" style="padding:40px 16px;">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:24px;overflow:hidden;box-shadow:0 4px 40px rgba(29,103,226,0.10);">
        <tr><td style="padding:20px 40px 16px;"><img src="http://iooo.pl/wp-content/uploads/2026/03/iooo_reuse_logo.png" alt="iooo ReUse Materials" height="54" style="display:block;height:54px;width:auto;border:0;"></td></tr>
        <tr><td style="background:linear-gradient(90deg,#8df2cd,#75cfee);height:3px;font-size:0;line-height:0;">&nbsp;</td></tr>
        <tr><td style="padding:32px 40px 24px;color:#1a1a1a;font-size:15px;line-height:1.75;font-family:Arial,sans-serif;">`;
}

/**
 * Returns the closing HTML of the branded email layout: closes the body cell, adds divider, footer cell with signature, closes all wrappers.
 * Use after <!-- BODY_END --> marker in templates.
 */
export function EMAIL_FOOTER_WRAPPER(user: UserLike): string {
  return `        </td></tr>
        <tr><td style="padding:0 40px;"><div style="height:1px;background:linear-gradient(90deg,#8df2cd,#75cfee);"></div></td></tr>
        <tr><td style="padding:20px 40px 28px;">${EMAIL_FOOTER(user)}</td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

/**
 * Email signature/footer HTML template.
 * Used in email body templates as: helpers.EMAIL_FOOTER(currentUser)
 */
export function EMAIL_FOOTER(user: UserLike): string {
  const name = user.fullName || user.full_name || '';
  const phone = user.phone || '';
  const email = user.email || '';
  return `<div dir="ltr" class="gmail_signature" data-smartmail="gmail_signature"><div dir="ltr"><table cellpadding="0" cellspacing="0" style="vertical-align:-webkit-baseline-middle;font-family:&quot;Trebuchet MS&quot;"><tbody><tr><td width="120" style="vertical-align:middle"><span style="margin-right:0px;display:block"><img src="http://iooo.pl/wp-content/uploads/2026/03/ioo-reuse-footer.png" width="130" style="max-width:130px"></span></td><td style="vertical-align:middle"><h3 color="#0d3d8a" style="margin:0px;font-size:16px;color:rgb(13,61,138)">${name}</h3><p color="#0d3d8a" style="margin:0px;color:rgb(13,61,138);font-size:12px;line-height:20px">iooo ReUse Materials sp. z o.o.</p><p color="#0d3d8a" style="margin:0px;color:rgb(13,61,138);font-size:12px;line-height:20px"><b>NIP</b>: 6793343441</p><p color="#0d3d8a" style="margin:0px;color:rgb(13,61,138);font-size:12px;line-height:20px"><b>BDO</b>: 000690506</p></td><td width="15"><div style="width:15px"></div></td><td color="#64F4CB" width="1" style="width:1px;border-bottom-style:none;border-left:1px solid rgb(100,244,203)"><br><br></td><td width="15"><div style="width:15px"></div></td><td style="vertical-align:middle"><table cellpadding="0" cellspacing="0" style="vertical-align:-webkit-baseline-middle"><tbody><tr height="25" style="vertical-align:middle"><td width="30" style="vertical-align:middle"><table cellpadding="0" cellspacing="0" style="vertical-align:-webkit-baseline-middle"><tbody><tr><td style="vertical-align:bottom"><span color="#64F4CB" width="11" style="display:block;background-color:rgb(100,244,203)"><img src="https://ci3.googleusercontent.com/meips/ADKq_NY-ugLnvhx8EKeU0NoFaQJxnwUBulA05De1Rj0lx5Itfo-At2BqiyZXrzXbvQpcbueon521QOzfNQ1y06xq7dVDLerq6ZY-nsYtDo9sdlsmC5ul1-T6XUQ4kEevfpeLQJDdM73VqUrTn1dnZ8Q_IA=s0-d-e1-ft#https://cdn2.hubspot.net/hubfs/53/tools/email-signature-generator/icons/phone-icon-2x.png" color="#64F4CB" width="13" style="display:block"></span></td></tr></tbody></table></td><td style="padding:0px;color:rgb(13,61,138)"><a href="tel:${phone}" color="#0d3d8a" style="color:rgb(13,61,138);font-size:12px" target="_blank">${phone}</a></td></tr><tr height="25" style="vertical-align:middle"><td width="30" style="vertical-align:middle"><table cellpadding="0" cellspacing="0" style="vertical-align:-webkit-baseline-middle"><tbody><tr><td style="vertical-align:bottom"><span color="#64F4CB" width="11" style="display:block;background-color:rgb(100,244,203)"><img src="https://ci3.googleusercontent.com/meips/ADKq_NZKllki1F8xHX3XP1B8cJ115cbaoAUYAu0XTemKLCDs4_mFQYcGkKTngars90NA25lBabg-0V6FL9Mdhi9cigSGVAoYg4fcRMPJxQoDUevRI9C9IJiurl0-cw3g5URKDFkoNJmeT24yAoCOJzjgkA=s0-d-e1-ft#https://cdn2.hubspot.net/hubfs/53/tools/email-signature-generator/icons/email-icon-2x.png" color="#64F4CB" width="13" style="display:block"></span></td></tr></tbody></table></td><td style="padding:0px"><a href="mailto:${email}" color="#0d3d8a" style="color:rgb(13,61,138);font-size:12px" target="_blank">${email}</a></td></tr><tr height="25" style="vertical-align:middle"><td width="30" style="vertical-align:middle"><table cellpadding="0" cellspacing="0" style="vertical-align:-webkit-baseline-middle"><tbody><tr><td style="vertical-align:bottom"><span color="#64F4CB" width="11" style="display:block;background-color:rgb(100,244,203)"><img src="https://ci3.googleusercontent.com/meips/ADKq_NZpTzogjkC8SImspe2VrkQmMEyAQ_UEsQ8i1uEACLP-dd1xSUs_1croi2dHo5RXs7nJOkvFUY80Zpf1eTx_3iKpY_UDsHkuFrSyvVcUrnU9zbR9Cpx9zTlPzBOfN7uWLFX1kakqikXKC89WGF1H2JO0=s0-d-e1-ft#https://cdn2.hubspot.net/hubfs/53/tools/email-signature-generator/icons/address-icon-2x.png" color="#64F4CB" width="13" style="display:block"></span></td></tr></tbody></table></td><td style="padding:0px"><span color="#0d3d8a" style="font-size:12px;color:rgb(13,61,138)">ul. Kącik 4, 30-549 Kraków</span></td></tr></tbody></table></td></tr></tbody></table><table cellpadding="0" cellspacing="0" style="color:rgb(34,34,34);vertical-align:-webkit-baseline-middle;font-family:&quot;Trebuchet MS&quot;;width:546px"><tbody><tr><td height="30"></td></tr><tr><td color="#64F4CB" height="1" style="width:569px;border-bottom:1px solid rgb(100,244,203);border-left-style:none;display:block"></td></tr><tr><td height="30"><br></td></tr></tbody></table></div></div>`;
}

/**
 * Format phone number with spaces for readability.
 * Keeps all characters, just inserts spaces: "+48123456789" → "+48 123 456 789".
 * Already-spaced numbers pass through unchanged.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  // Strip non-digit/non-plus chars for parsing, keep + prefix
  const digits = phone.replace(/[^\d+]/g, '');
  if (!digits) return phone;
  // Split: +CC then groups of 3
  const match = digits.match(/^(\+?\d{1,2})(\d+)$/);
  if (!match) return phone;
  const cc = match[1];
  const rest = match[2].replace(/(\d{3})(?=\d)/g, '$1 ');
  return `${cc} ${rest}`;
}

/**
 * Format driver details as a clean string, omitting null/empty fields.
 * lang='pl' uses "rej:" prefix for plate; lang='en' uses "reg:".
 * Usage in resolver: helpers.formatDriverDetails(transports[0])
 * Usage in resolver (EN): helpers.formatDriverDetails(transports[0], "en")
 */
export function formatDriverDetails(transport: TransportLike | null | undefined, lang: 'pl' | 'en' = 'pl'): string {
  if (!transport) return '';
  const plateLabel = lang === 'en' ? 'reg' : 'rej';
  return [
    transport.driverName || null,
    transport.driverPhone ? `tel: ${transport.driverPhone}` : null,
    transport.vehiclePlate ? `${plateLabel}: ${transport.vehiclePlate}` : null,
  ].filter(Boolean).join(', ');
}

/**
 * Extend a company object with country name_en and name_pl resolved from a countries dictionary.
 * Usage in resolver: helpers.withCountry(loadCommercialCompany, countries)
 * Adds `countryNameEn` and `countryNamePl` fields based on `countryCode`.
 */
export function withCountry(company: CompanyLike | null | undefined, countries: CountryLike[]): CompanyLike | null {
  if (!company) return null;
  if (!countries || !Array.isArray(countries)) return company;
  const code = company.countryCode;
  if (!code) return company;
  const country = countries.find((c) => c.code === code);
  if (!country) return company;
  return {
    ...company,
    countryNameEn: country.nameEn,
    countryNamePl: country.namePl,
  };
}

/**
 * Resolve transit field to comma-separated country names.
 * Handles both country codes ("BG,CY,DK") and full names ("Germany").
 * If a token matches a country code, it's resolved to nameEn; otherwise kept as-is.
 */
export function resolveTransit(transit: string | null | undefined, countries: CountryLike[], lang: 'en' | 'pl' = 'en'): string {
  if (!transit) return '';
  const tokens = transit.split(',').map((t) => t.trim()).filter(Boolean);
  const nameField = lang === 'pl' ? 'namePl' : 'nameEn';
  return tokens.map((token) => {
    // Try as country code (2-letter uppercase)
    const country = countries.find((c) => c.code === token.toUpperCase());
    if (country) return country[nameField] || token;
    return token; // already a name
  }).join(', ');
}

/**
 * Get product name translated to the given language.
 * Uses invoice_names_unload (or _load) keyed by lang code, falls back to pl → name.
 * Usage in resolver: helpers.productName(product, "cs")
 *                    helpers.productName(product, "pl", { lower: true })
 * Usage in Handlebars: {{productName product emailLanguage}}
 *                      {{productName product emailLanguage "lower"}}
 */
export function productName(product: ProductLike | null | undefined, lang: string = 'pl', options?: { lower?: boolean }): string {
  if (!product) return '';
  const names = product.invoiceNamesUnload || product.invoiceNamesLoad || {};
  let name = names[lang] || names['en'] || names['pl'] || product.namePl || product.name || '';
  if (options?.lower && name) {
    name = name.charAt(0).toLowerCase() + name.slice(1);
  }
  return name;
}

/**
 * Format weight with 3 decimal places and comma separator.
 * 23.91 → "23,910", null → ""
 */
export function formatWeight(value: unknown): string {
  if (value == null) return '';
  const num = Number(value);
  if (isNaN(num)) return '';
  return num.toFixed(3).replace('.', ',');
}

/**
 * Format a number without decimal places.
 * Usage in resolver: helpers.formatNumber(12345.67) → "12345"
 * Usage in Handlebars: {{formatNumber someValue}}
 */
export function formatNumber(value: unknown): string {
  if (value == null) return '';
  const num = Number(value);
  if (isNaN(num)) return '';
  return Math.round(num).toString();
}

/**
 * Format price with currency and optional unit suffix.
 * per_ton → "1250.00 EUR/Mg", flat_rate → "500.00 PLN" (no unit).
 * Usage in resolver: helpers.formatPrice(transport)
 * Also works for transaction pricing objects with price/priceCurrency/priceType fields.
 */
export function formatPrice(obj: PriceLike | null | undefined): string {
  if (!obj || obj.price == null) return '';
  const abs = Math.abs(Math.round(obj.price * 100) / 100);
  const formatted = abs.toFixed(2);
  const currency = obj.priceCurrency || obj.price_currency || '';
  const type = obj.priceType || obj.price_type || '';
  if (type === 'per_ton' || type === 'per_tone') {
    return `${formatted} ${currency}/Mg`;
  }
  return `${formatted} ${currency}`;
}

const PAYMENT_TERMS_LABEL: Record<string, string> = {
  pl: 'dni', cs: 'dní', sk: 'dní', en: 'days',
  de: 'Tage', fr: 'jours', es: 'días', it: 'giorni', pt: 'dias', nl: 'dagen',
  sv: 'dagar', da: 'dage', fi: 'päivää', et: 'päeva', lv: 'dienas', lt: 'dienų',
  ro: 'zile', bg: 'дни', el: 'ημέρες', hr: 'dana', sl: 'dni', hu: 'nap',
  ga: 'lá', mt: 'jiem',
};

/**
 * Format payment terms as "X dni" / "X dní" / "X days".
 */
export function formatPaymentTerms(days: number | null | undefined, lang: string = 'pl'): string {
  if (days == null) return '';
  const label = PAYMENT_TERMS_LABEL[lang] || PAYMENT_TERMS_LABEL.pl;
  return `${Math.round(days)} ${label}`;
}

/**
 * Enrich a company object with all commonly needed derived fields.
 * Returns null if company is null/undefined.
 *
 * Adds: address, contactPerson, transportNotificationEmails,
 *       weightConfirmationEmails, countryNamePl, countryNameEn,
 *       formattedPaymentTerms.
 */
export function enrichCompany(company: CompanyLike | null | undefined, countries?: CountryLike[]): CompanyLike | null {
  if (!company) return null;

  const result: CompanyLike = { ...company };

  // Formatted address
  result.address = formatCompanyAddress(company);

  // Document contact person (on_annex_doc)
  result.contactPerson = docContact(company);

  // Contact email lists by purpose
  result.transportNotificationEmails = getContactEmails(company, 'transport_notification');
  result.weightConfirmationEmails = getContactEmails(company, 'weight_confirmation');

  // Payment terms
  result.formattedPaymentTerms = formatPaymentTerms(company.paymentTerms);

  // Country names + language from lookup
  if (countries && Array.isArray(countries) && company.countryCode) {
    const country = countries.find((c) => c.code === company.countryCode);
    if (country) {
      result.countryNameEn = country.nameEn;
      result.countryNamePl = country.namePl;
      result.languageCode = country.languageCode;
    }
  }

  return result;
}

/**
 * Enrich a transport record into a flat carrier object:
 * enriched transport company fields + transport details + formatted price.
 * Returns null if transport is null/undefined.
 */
export function enrichCarrier(transport: TransportLike | null | undefined, countries?: CountryLike[]): CompanyLike | null {
  if (!transport) return null;

  const company = enrichCompany(transport.transportCompany, countries);

  return {
    ...(company || {}),
    driverName: transport.driverName ?? null,
    driverPhone: transport.driverPhone ?? null,
    driverPhoneFormatted: formatPhone(transport.driverPhone),
    vehiclePlate: transport.vehiclePlate ?? null,
    containerNumber: transport.containerNumber ?? null,
    transportType: transport.transportType ?? null,
    sequenceNumber: transport.sequenceNumber ?? null,
    price: transport.price ?? null,
    priceCurrency: transport.priceCurrency ?? null,
    priceType: transport.priceType ?? null,
    formattedPrice: formatPrice(transport),
  };
}

/**
 * Returns the full helpers scope object to be passed into sandboxed resolvers.
 */
export function getHelperScope() {
  return {
    formatDate,
    getDayLocative,
    getDayName,
    formatCompanyAddress,
    formatTransportMeans,
    getContactEmails,
    docContact,
    withDocContact,
    withCountry,
    filterFiles,
    selectApprovedFile,
    EMAIL_FOOTER,
    EMAIL_HEADER,
    EMAIL_FOOTER_WRAPPER,
    formatDriverDetails,
    isEuCountry,
    nationalCode,
    formatNumber,
    formatPrice,
    enrichCompany,
    enrichCarrier,
    formatTime,
    formatPaymentTerms,
    formatPhone,
    formatWeight,
    productName,
    resolveTransit,
  };
}
